/**
 * MeetingsSection - Meetings list with expand/collapse functionality
 */

import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { useDashboardSectionPref } from '@/hooks/useDashboardSectionPref';
import { useRefreshAt } from '@/hooks/useRefreshAt';
import { Button } from '@/lib/base/buttons/button';
import { markOnce } from '@/lib/boot/bootMetrics';
import { useOpenNoteCompose } from '@/lib/crm/record/notes/useNoteTabActions';
import type { MeetingsView } from '@/lib/localStorage/dashboardSectionPrefs';
import type { TimeFormat } from '@/lib/time';
import {
  calendarDateIn,
  isHour12,
  startOfCalendarDayIn,
  timeParts,
  useOrgTimeFormat,
} from '@/lib/time';
import { DayView } from '@/pages/calendar/components/DayView';
import { isEventPast } from '@/pages/calendar/utils/calendarUtils';
import type { CalendarEvent } from '@/pages/calendar/utils/eventMapper';
import { mapCrmObjectsToCalendarEvents } from '@/pages/calendar/utils/eventMapper';
import type { CrmObject } from '@/types/crm';

import { MAX_VISIBLE_TASKS } from './constants';
import { useExpandableSection } from './hooks/useExpandableSection';
import { MeetingRow } from './MeetingRow';
import { ReminderRow } from './ReminderRow';
import type { SectionVariantOption } from './SectionVariantDropdown';
import { SectionVariantDropdown } from './SectionVariantDropdown';
import { TaskRow } from './TaskRow';
import { createResearchQueryConfig } from './utils/meetingResearchQuery';

/** Maximum number of meetings to show before "Show more" */
const MAX_VISIBLE_MEETINGS = 3;

const MEETINGS_VARIANT_OPTIONS = [
  { value: 'compact', label: 'List' },
  { value: 'dayview', label: 'Day' },
] as const satisfies readonly SectionVariantOption<MeetingsView>[];

/**
 * Filter events to today only (no expiry filter).
 * Uses the provided timezone for day-boundary comparison.
 */
function filterEventsToToday(
  events: CalendarEvent[],
  timeZone: string
): CalendarEvent[] {
  const todayStr = calendarDateIn(new Date(), timeZone);
  return events.filter(event => {
    const eventDayStr = calendarDateIn(new Date(event.start_time), timeZone);
    return eventDayStr === todayStr;
  });
}

/**
 * Filter events to today only, hiding meetings whose end_time has passed.
 * Uses the provided timezone for day-boundary comparison.
 *
 * Delegates past-ness to the shared `isEventPast` helper, which reads
 * `Date.now()` internally rather than accepting a pre-captured instant. The
 * only guarantee that matters here is that this filter and
 * `filterTodayAndExpired` partition events consistently — `isEventPast` is
 * called for each event within the same synchronous pass, so any drift
 * between calls is on the order of microseconds and immaterial. The
 * scheduling calculation below (`getNextMeetingExpiryTime`) still uses the
 * render's pre-captured `now`.
 */
function filterTodayAndUnexpired(
  events: CalendarEvent[],
  timeZone: string
): CalendarEvent[] {
  return filterEventsToToday(events, timeZone).filter(
    event => !isEventPast(event)
  );
}

/**
 * Filter events to today's past meetings (end_time has passed).
 * Used to render a "Earlier today" section below the upcoming meetings.
 * See `filterTodayAndUnexpired` for the past-ness / instant-consistency note.
 */
function filterTodayAndExpired(
  events: CalendarEvent[],
  timeZone: string
): CalendarEvent[] {
  return filterEventsToToday(events, timeZone).filter(event =>
    isEventPast(event)
  );
}

/**
 * Get the next timestamp when a meeting transitions from upcoming to
 * "Earlier today" — i.e. the earliest end_time that is still in the future.
 * Returns null when no future transitions remain.
 * @param now Current timestamp in ms, pre-captured by the caller so the
 * scheduling calculation and both partition filters evaluate against the
 * same instant within a render.
 */
function getNextMeetingExpiryTime(
  events: CalendarEvent[],
  timeZone: string,
  now: number
): number | null {
  let earliest: number | null = null;
  const todayEvents = filterEventsToToday(events, timeZone);

  for (const event of todayEvents) {
    const expiryTime = new Date(event.end_time).getTime();
    if (expiryTime > now) {
      if (earliest === null || expiryTime < earliest) {
        earliest = expiryTime;
      }
    }
  }

  return earliest;
}

/**
 * Convert an Intl-formatted hour/dayPeriod pair to an unambiguous 24-hour
 * (0-23) value. Needed because getTimeZoneOffsetMs must reconstruct the real
 * wall-clock hour for date-boundary math regardless of the org's 12h/24h
 * display preference (unlike a *displayed* time, this internal calculation
 * must never be ambiguous).
 */
function to24Hour(hour: number, dayPeriod: string | undefined): number {
  if (dayPeriod === 'AM') {
    return hour === 12 ? 0 : hour;
  }
  if (dayPeriod === 'PM') {
    return hour === 12 ? 12 : hour + 12;
  }
  // Intl renders midnight as "24" in some environments.
  return hour % 24;
}

/**
 * Offset (ms) between the target timezone's wall clock and UTC at `date` —
 * i.e. (wall-clock interpreted as UTC) − (actual UTC). Positive east of UTC.
 * Read from `Intl` so it reflects the zone's real DST state at that instant.
 */
function getTimeZoneOffsetMs(
  date: Date,
  timeZone: string,
  format: TimeFormat
): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...timeParts(format),
    second: '2-digit',
  }).formatToParts(date);
  const get = (type: string) => parts.find(part => part.type === type)?.value;
  const getNum = (type: string) => Number(get(type) ?? '0');
  const hour24 = isHour12(format)
    ? to24Hour(getNum('hour'), get('dayPeriod'))
    : getNum('hour') % 24;
  const asUtc = Date.UTC(
    getNum('year'),
    getNum('month') - 1,
    getNum('day'),
    hour24,
    getNum('minute'),
    getNum('second')
  );
  return asUtc - date.getTime();
}

/**
 * Get the timestamp of the next day boundary (midnight) in the given
 * timezone. Used as a fallback refresh target so "today" filtering
 * refreshes when the day rolls over even if no meeting expiry is pending.
 *
 * `timeZone` is the profile timezone, which the backend guarantees is a valid
 * IANA zone — so there is no invalid-zone case to catch and no browser-local
 * fallback to disagree with the rest of this file.
 *
 * Computes the actual next wall-clock midnight in the target timezone rather
 * than adding a fixed 24h, so it lands on true local midnight across DST
 * transitions (where a calendar day is 23 or 25 hours long).
 */
function getNextDayBoundary(timeZone: string, format: TimeFormat): number {
  // Next midnight is the start of tomorrow's date; Date.UTC normalizes day
  // overflow (e.g. the 31st → the 1st of the next month).
  const [year, month, day] = calendarDateIn(new Date(), timeZone)
    .split('-')
    .map(Number);
  const tomorrowAsUtc = Date.UTC(year, month - 1, day + 1);
  // tomorrowAsUtc treats wall-clock midnight as if it were UTC; correct it by
  // the zone's offset at that instant to get the real UTC timestamp.
  return (
    tomorrowAsUtc -
    getTimeZoneOffsetMs(new Date(tomorrowAsUtc), timeZone, format)
  );
}

interface MeetingsSectionProps {
  /** Calendar events for the fetched range */
  calendarEvents: CalendarEvent[];
  /** Organization ID for API calls */
  orgId: number;
  /** Integration entity IDs the meetings filter is built from. Empty array indicates the user has no connected integrations. */
  integrationEntityIds: string[];
  /** Whether calendar data is still loading */
  isLoading?: boolean;
  /** Whether the dashboard is processing (used for visual feedback) */
  isProcessing?: boolean;
  /**
   * IANA timezone for time display and day-boundary comparison (e.g.
   * "America/New_York") — the user's profile timezone, which the backend
   * always resolves. Required: there is no browser-local fallback.
   */
  timeZone: string;
  /** Today's reminder objects */
  reminders?: CrmObject[];
  /** Associated objects for reminders (keyed by entity_id) */
  remindersAssociatedObjects?: Record<string, CrmObject>;
  /** Today's task objects due today */
  tasks?: CrmObject[];
  /** Associated objects for tasks (keyed by entity_id) */
  tasksAssociatedObjects?: Record<string, CrmObject>;
}

/** Skeleton row for loading state - matches MeetingRow height */
function MeetingRowSkeleton() {
  return (
    <div className='h-14 bg-primary border border-secondary rounded-[4px] animate-pulse' />
  );
}

/**
 * MeetingsSection - Main meetings section component
 */
export function MeetingsSection({
  calendarEvents,
  orgId,
  integrationEntityIds,
  isLoading = false,
  isProcessing = false,
  timeZone,
  reminders = [],
  remindersAssociatedObjects = {},
  tasks = [],
  // Currently unused: TaskRow no longer takes associatedObjects (see CH1 in
  // the task-surfaces PR review) — kept in the prop type for the caller's
  // existing plumbing until that upstream fetch is removed.
  tasksAssociatedObjects: _tasksAssociatedObjects = {},
}: MeetingsSectionProps) {
  const { timeFormat } = useOrgTimeFormat();
  // Track which meetings are expanded (Set allows multiple expansions)
  const { toggleExpanded, isExpanded, expand } = useExpandableSection();
  // "Show more" expands to reveal all meetings for the rest of today
  const [showAll, setShowAll] = useState(false);
  // 'Show more' for the 'Earlier today' past meetings section (independent from upcoming `showAll`)
  const [showAllPast, setShowAllPast] = useState(false);
  // 'Show more' for tasks — independent of the meetings/reminders `showAll` and
  // the past `showAllPast` toggles.
  const [showAllTasks, setShowAllTasks] = useState(false);

  const [meetingsView, setMeetingsView] = useDashboardSectionPref(
    'meetings',
    'compact'
  );

  // Perf trace: first render with real content (skeleton rows gone). Counts
  // only — never row contents.
  useEffect(() => {
    if (!isLoading) {
      markOnce('section:meetings:content', {
        meetings: calendarEvents.length,
        reminders: reminders.length,
        tasks: tasks.length,
      });
    }
  }, [isLoading, calendarEvents.length, reminders.length, tasks.length]);

  // Captured once per render for the scheduling calculation below
  // (getNextMeetingExpiryTime). The partition filters delegate past-ness to
  // the shared isEventPast helper, which reads Date.now() internally — see
  // filterTodayAndUnexpired for why that's fine here.
  const now = Date.now();

  // Schedule a re-render at whichever comes first: the next meeting expiry
  // (upcoming -> "Earlier today") or the next day boundary (so "today"
  // filtering refreshes after midnight even with no pending expiry). The
  // re-render recalculates the time-dependent values below (both the compact
  // filters and the dayview values) with the current time.
  useRefreshAt(
    Math.min(
      getNextMeetingExpiryTime(calendarEvents, timeZone, now) ?? Infinity,
      getNextDayBoundary(timeZone, timeFormat)
    )
  );

  // Filter events to today only, hiding expired meetings (compact variant).
  // Not memoized: the filter is cheap (~10 events) and must reflect the
  // current time whenever useRefreshAt triggers a re-render.
  const filteredEvents = filterTodayAndUnexpired(calendarEvents, timeZone);

  // Past meetings — ended (end_time passed), shown in "Earlier today" section
  const pastEvents = filterTodayAndExpired(calendarEvents, timeZone);

  const hasMorePastEvents = pastEvents.length > MAX_VISIBLE_MEETINGS;

  const visiblePastEvents = useMemo(() => {
    if (showAllPast || !hasMorePastEvents) {
      return pastEvents;
    }
    return pastEvents.slice(0, MAX_VISIBLE_MEETINGS);
  }, [showAllPast, hasMorePastEvents, pastEvents]);

  const hiddenPastCount = pastEvents.length - visiblePastEvents.length;

  // Note compose — opens the floating compose panel. Adding a note creates an
  // ahoy_note record, so the row only gets the affordance when this principal
  // holds that grant (a viewer never does); the gate lives in the shared hook.
  const { openNoteCompose, canCreateNote } = useOpenNoteCompose();

  const handleAddNote = useCallback(
    (meetingEntityId: string) => {
      openNoteCompose({
        linkedEntities: [
          { entityId: meetingEntityId, entityTypeName: 'ahoy_meeting' },
        ],
      });
    },
    [openNoteCompose]
  );
  const onAddNote = canCreateNote ? handleAddNote : undefined;

  const handleShowAll = useCallback(() => setShowAll(true), []);
  const handleShowAllPast = useCallback(() => setShowAllPast(true), []);
  const handleShowAllTasks = useCallback(() => setShowAllTasks(true), []);

  // Filter events to today only (no expiry filter, for day view),
  // and include reminders mapped to CalendarEvent format so they
  // render as all-day events in the DayView header strip.
  // Not memoized: `filterEventsToToday` reads `new Date()`, so this must
  // reflect the current time whenever useRefreshAt triggers a re-render —
  // a memo keyed only on the inputs would keep yesterday's events after
  // midnight. The filter + reminder map is cheap (~10 events) and DayView is
  // not memoized, so a fresh array reference each render costs nothing.
  const filteredEventsForDayView = [
    ...mapCrmObjectsToCalendarEvents(reminders, remindersAssociatedObjects),
    ...filterEventsToToday(calendarEvents, timeZone),
  ];

  // Today's date in the user's timezone for the DayView date prop. Not
  // memoized for the same reason: it reads `new Date()` and must roll over to
  // the new date when useRefreshAt fires at the day boundary.
  // DayView reads its `date` as a calendar day IN `timeZone`, so this must be
  // that day's real local midnight — a browser-local midnight would be the
  // adjacent day for anyone whose profile zone isn't their laptop's.
  const todayInUserTz = startOfCalendarDayIn(
    calendarDateIn(new Date(), timeZone),
    timeZone
  );

  const meetingItemCount = reminders.length + filteredEvents.length;
  const hasMoreMeetings = meetingItemCount > MAX_VISIBLE_MEETINGS;

  // Tasks have their own MAX_VISIBLE_TASKS cap (see below) and no longer
  // consume the meetings/reminders budget below — that budget is computed
  // from reminders + meetings only, so a long task list can no longer
  // starve meetings/reminders out of their visible slots.
  const visibleReminders = useMemo(() => {
    if (showAll || !hasMoreMeetings) {
      return reminders;
    }
    return reminders.slice(0, MAX_VISIBLE_MEETINGS);
  }, [showAll, hasMoreMeetings, reminders]);

  const visibleEvents = useMemo(() => {
    if (showAll || !hasMoreMeetings) {
      return filteredEvents;
    }
    const remaining = Math.max(
      0,
      MAX_VISIBLE_MEETINGS - visibleReminders.length
    );
    return filteredEvents.slice(0, remaining);
  }, [showAll, hasMoreMeetings, filteredEvents, visibleReminders]);

  const hiddenCount =
    meetingItemCount - visibleReminders.length - visibleEvents.length;
  const firstMeetingId = visibleEvents[0]?.id;

  const hasMoreTasks = tasks.length > MAX_VISIBLE_TASKS;
  const visibleTasks = useMemo(() => {
    if (showAllTasks || !hasMoreTasks) {
      return tasks;
    }
    return tasks.slice(0, MAX_VISIBLE_TASKS);
  }, [showAllTasks, hasMoreTasks, tasks]);
  const hiddenTaskCount = tasks.length - visibleTasks.length;

  const {
    data: firstMeetingResearch,
    isLoading: firstMeetingResearchLoading,
    error: firstMeetingResearchError,
  } = useQuery({
    ...createResearchQueryConfig(orgId, firstMeetingId),
    enabled: !isLoading && meetingsView === 'compact' && !!firstMeetingId,
  });

  const evaluatedFirstIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!firstMeetingId) {
      return;
    }
    if (meetingsView !== 'compact') {
      return;
    }
    if (evaluatedFirstIdsRef.current.has(firstMeetingId)) {
      return;
    }
    if (firstMeetingResearchLoading || firstMeetingResearchError) {
      return;
    }
    if (!firstMeetingResearch || firstMeetingResearch.length === 0) {
      return;
    }
    evaluatedFirstIdsRef.current.add(firstMeetingId);
    expand(firstMeetingId);
  }, [
    firstMeetingId,
    meetingsView,
    firstMeetingResearch,
    firstMeetingResearchLoading,
    firstMeetingResearchError,
    expand,
  ]);

  const sectionTitle =
    reminders.length > 0 || tasks.length > 0
      ? "What's happening today"
      : "Today's meetings";

  // Header with "View calendar" link (per Figma design)
  const renderHeader = () => (
    <div className='flex items-center justify-between mt-0.5 mb-3'>
      <h2 className='text-[15px] font-semibold text-primary'>{sectionTitle}</h2>
      <div className='flex items-center gap-3'>
        <SectionVariantDropdown
          sectionKey='meetings'
          options={MEETINGS_VARIANT_OPTIONS}
          value={meetingsView}
          onChange={setMeetingsView}
          ariaLabel={`${sectionTitle} view`}
        />
        <Link
          to={`/${orgId}/calendar`}
          className='text-[12px] font-semibold text-primary hover:text-tertiary transition-colors'
        >
          View calendar
        </Link>
      </div>
    </div>
  );

  // Loading state - show skeleton rows to prevent layout shift
  if (isLoading) {
    return (
      <div className='space-y-3' data-processing={isProcessing}>
        {renderHeader()}
        <div className='space-y-1.5'>
          <MeetingRowSkeleton />
          <MeetingRowSkeleton />
          <MeetingRowSkeleton />
        </div>
      </div>
    );
  }

  // Dayview variant — renders full day grid regardless of empty state
  if (meetingsView === 'dayview') {
    return (
      <div className='space-y-3' data-processing={isProcessing}>
        {renderHeader()}
        <div className='h-[480px] overflow-hidden rounded-[4px] border border-secondary bg-primary'>
          <DayView
            events={filteredEventsForDayView}
            date={todayInUserTz}
            timezone={timeZone}
          />
        </div>
      </div>
    );
  }

  // Empty state — shown when neither tasks, reminders, nor meetings (upcoming or past) are present
  if (
    tasks.length === 0 &&
    reminders.length === 0 &&
    filteredEvents.length === 0 &&
    pastEvents.length === 0
  ) {
    // Variant 1: user has no connected integrations — show 'Connect a calendar' CTA
    if (integrationEntityIds.length === 0) {
      return (
        <div className='space-y-3' data-processing={isProcessing}>
          {renderHeader()}
          <div className='flex flex-col items-center gap-3 pt-1.5 pb-0 sm:py-8'>
            <p className='text-[13px] text-tertiary text-center'>
              Connect a calendar to see today&apos;s meetings.
            </p>
            <Link
              to={`/${orgId}/settings/email-calendar`}
              className='text-[13px] font-medium text-fg-brand-primary hover:underline'
            >
              Connect calendar
            </Link>
          </div>
        </div>
      );
    }

    // Variant 2: user has integrations but nothing today
    return (
      <div className='space-y-3' data-processing={isProcessing}>
        {renderHeader()}
        <div className='text-center pt-1.5 pb-0 sm:py-8 text-[13px] text-tertiary'>
          No reminders or meetings today
        </div>
      </div>
    );
  }

  // Normal state with reminders and/or meetings
  return (
    <div className='space-y-3' data-processing={isProcessing}>
      {renderHeader()}
      <div className='space-y-1.5'>
        {visibleTasks.map(task => (
          <TaskRow key={task.entity_id} task={task} />
        ))}
        {hasMoreTasks && !showAllTasks && (
          <div className='flex justify-center pt-1 -mb-2'>
            <Button
              color='link-color'
              size='sm'
              onClick={handleShowAllTasks}
              aria-label={`Show ${hiddenTaskCount} more tasks`}
            >
              Show {hiddenTaskCount} more
            </Button>
          </div>
        )}
        {visibleReminders.map(reminder => (
          <ReminderRow
            key={reminder.entity_id}
            reminder={reminder}
            associatedObjects={remindersAssociatedObjects}
          />
        ))}
        {visibleEvents.map(event => (
          <MeetingRow
            key={event.id}
            event={event}
            isExpanded={isExpanded(event.id)}
            onToggle={toggleExpanded}
            orgId={orgId}
            meetingEntityId={event.id}
            // TODO: Add company badge. Blocked on: backend must expose event.associated_company_id and company.name in CalendarEvent serializer. Owner: backend team. ETA: Sprint 12.
            dealStageEntityId={undefined}
            companyEntityId={undefined}
            timeZone={timeZone}
            onAddNote={onAddNote}
          />
        ))}
        {hasMoreMeetings && !showAll && (
          <div className='flex justify-center pt-1 -mb-2'>
            <Button
              color='link-color'
              size='sm'
              onClick={handleShowAll}
              aria-label={`Show ${hiddenCount} more meetings`}
            >
              Show {hiddenCount} more
            </Button>
          </div>
        )}
      </div>

      {/* Past meetings — ended (end_time passed), visually faded */}
      {pastEvents.length > 0 && (
        <div className='space-y-1.5 pt-1 opacity-60'>
          <h3 className='text-[13px] font-medium text-tertiary'>
            Earlier today
          </h3>
          {visiblePastEvents.map(event => (
            <MeetingRow
              key={event.id}
              event={event}
              isExpanded={isExpanded(event.id)}
              onToggle={toggleExpanded}
              orgId={orgId}
              meetingEntityId={event.id}
              dealStageEntityId={undefined}
              companyEntityId={undefined}
              timeZone={timeZone}
              onAddNote={onAddNote}
            />
          ))}
          {hasMorePastEvents && !showAllPast && (
            <div className='flex justify-center pt-1 -mb-2'>
              <Button
                color='link-color'
                size='sm'
                onClick={handleShowAllPast}
                aria-label={`Show ${hiddenPastCount} more earlier meetings`}
              >
                Show {hiddenPastCount} more
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
